<script setup lang="ts">
import {
  useSeoBase,
  useJsonLd,
  orgNode,
  localBusinessNode,
  breadcrumbNode,
} from "../../composables/useSeo";
import { usePhoneNumber } from "../../composables/usePhoneNumber";
import { useAnalytics } from "../../composables/useAnalytics";

const { phoneNumber, phoneNumberPretty, phoneNumberTel } = usePhoneNumber();
const { trackConversion } = useAnalytics();
const { servicePrice, serviceLabel, loadPrice } = usePricing();
const displayedServicePrice = servicePrice("junk-removal");
const displayedServiceLabel = serviceLabel("junk-removal");
const quarterTrailerPrice = loadPrice("quarter-trailer");
const halfTrailerPrice = loadPrice("half-trailer");
const fullTrailerPrice = loadPrice("full-trailer");

useSeoBase({
  title: "Junk Removal Services | Junk Guys MN - Same Day Pickup Available",
  description:
    "Professional junk removal services across the Twin Cities. Same-day pickup available for furniture, electronics, clothing, and household items. Get your space back today!",
  canonical: "https://junkguysmn.com/services/junk-removal",
  ogImage: "https://junkguysmn.com/og.jpg",
});

useJsonLd([
  orgNode(),
  localBusinessNode(),
  breadcrumbNode([
    { name: "Home", url: "https://junkguysmn.com" },
    { name: "Services", url: "https://junkguysmn.com/services" },
    {
      name: "Junk Removal",
      url: "https://junkguysmn.com/services/junk-removal",
    },
  ]),
  {
    "@type": "Service",
    name: "Junk Removal",
    description:
      "Professional junk removal services for residential and commercial properties",
    provider: {
      "@type": "Organization",
      name: "Junk Guys MN",
    },
    areaServed: {
      "@type": "Place",
      name: "Minneapolis-St. Paul Twin Cities Metro Area",
    },
    serviceType: "Junk Removal",
  },
]);
</script>

<template>
  <div class="min-h-screen bg-slate-50">
    <!-- Hero Section -->
    <section
      class="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white"
    >
      <div class="mx-auto max-w-7xl px-4 py-16 lg:py-24">
        <div class="text-center max-w-3xl mx-auto">
          <h1 class="text-4xl lg:text-6xl font-bold mb-6">
            Junk Removal in the Twin Cities
          </h1>

          <!-- Service Intro Paragraph -->
          <div
            class="mt-6 text-left max-w-2xl mx-auto bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20"
          >
            <p class="text-lg text-slate-100 leading-relaxed">
              Junk Guys MN provides professional junk removal services
              throughout Minneapolis, St. Paul, and the greater Twin Cities
              area. Whether you need same-day pickup for a single item or a
              complete property cleanout, our experienced crew handles it all
              with care and efficiency.
            </p>
          </div>

          <div class="mt-8 flex flex-wrap justify-center gap-4">
            <a
              :href="phoneNumberTel"
              @click="trackConversion"
              class="inline-flex items-center justify-center px-8 py-4 bg-primary-main hover:bg-primary-dark text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              📞 Call {{ phoneNumberPretty }}
            </a>
            <NuxtLink
              to="/book"
              class="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-slate-900 transition-all duration-200"
            >
              📅 Book Online
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- Service Overview -->
    <section class="py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 class="text-3xl font-bold text-slate-900 mb-6">
              What This Service Includes
            </h2>
            <p class="text-lg text-slate-600 mb-6">
              Our junk removal service handles everything from small household
              items to large furniture pieces throughout Minneapolis, St. Paul,
              and the Twin Cities. We provide full-service hauling and
              responsible disposal for any type of residential or commercial
              junk.
            </p>
            <div class="grid grid-cols-2 gap-4">
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Furniture</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Electronics</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Clothing</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Books & Toys</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Kitchen Items</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Storage Items</span>
              </div>
            </div>
          </div>
          <div class="bg-white rounded-2xl shadow-lg p-8">
            <h3 class="text-2xl font-bold text-slate-900 mb-6">
              Service Details
            </h3>
            <div class="space-y-4">
              <div
                class="flex justify-between items-center p-4 bg-slate-50 rounded-lg"
              >
                <span class="font-semibold text-slate-700">{{
                  displayedServiceLabel
                }}</span>
                <span class="text-2xl font-bold text-primary-main">{{
                  displayedServicePrice
                }}</span>
              </div>
              <div
                class="flex justify-between items-center p-4 bg-slate-50 rounded-lg"
              >
                <span class="font-semibold text-slate-700"
                  >Typical Duration</span
                >
                <span class="text-lg text-slate-600">1-3 hours</span>
              </div>
              <div
                class="flex justify-between items-center p-4 bg-slate-50 rounded-lg"
              >
                <span class="font-semibold text-slate-700"
                  >Same-Day Service</span
                >
                <span class="text-green-600 font-semibold">Available</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- How It Works -->
    <section class="bg-white py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            How Our Process Works
          </h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Simple, transparent process from start to finish
          </p>
        </div>
        <div class="grid md:grid-cols-3 gap-8">
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl font-bold text-primary-main">1</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Get a Quote
            </h3>
            <p class="text-slate-600">
              Text us photos or book an on-site quote. We'll give you a fair,
              transparent price based on volume.
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl font-bold text-primary-main">2</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              We Handle Everything
            </h3>
            <p class="text-slate-600">
              Our crew arrives on time, carefully removes your items, and leaves
              your space broom-clean.
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl font-bold text-primary-main">3</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Responsible Disposal
            </h3>
            <p class="text-slate-600">
              We donate usable items and recycle when possible. Only true waste
              goes to the landfill.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing -->
    <section class="bg-slate-50 py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Pricing – What Affects Cost
          </h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Our pricing is based on the amount of space your junk takes up in
            our truck
          </p>
        </div>
        <div class="grid md:grid-cols-3 gap-8">
          <div class="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div class="text-4xl mb-4">📦</div>
            <h3 class="text-xl font-bold text-slate-900 mb-2">Small Load</h3>
            <div class="text-3xl font-bold text-primary-main mb-2">
              {{ quarterTrailerPrice }}
            </div>
            <p class="text-slate-600 mb-4">About 1/4 truck</p>
            <ul class="text-left space-y-2 text-sm text-slate-600">
              <li>• Small furniture pieces</li>
              <li>• Electronics & appliances</li>
              <li>• Boxes & bags of items</li>
            </ul>
          </div>
          <div
            class="bg-white rounded-2xl shadow-lg p-8 text-center border-2 border-primary-main"
          >
            <div class="text-4xl mb-4">🚛</div>
            <h3 class="text-xl font-bold text-slate-900 mb-2">Medium Load</h3>
            <div class="text-3xl font-bold text-primary-main mb-2">
              {{ halfTrailerPrice }}
            </div>
            <p class="text-slate-600 mb-4">About 1/2 truck</p>
            <ul class="text-left space-y-2 text-sm text-slate-600">
              <li>• Multiple furniture pieces</li>
              <li>• Room cleanout</li>
              <li>• Garage organization</li>
            </ul>
          </div>
          <div class="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div class="text-4xl mb-4">🏠</div>
            <h3 class="text-xl font-bold text-slate-900 mb-2">Full Load</h3>
            <div class="text-3xl font-bold text-primary-main mb-2">
              {{ fullTrailerPrice }}
            </div>
            <p class="text-slate-600 mb-4">Full truck capacity</p>
            <ul class="text-left space-y-2 text-sm text-slate-600">
              <li>• Complete room cleanout</li>
              <li>• Large furniture sets</li>
              <li>• Major decluttering</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Factors -->
    <section class="bg-white py-16 border-t border-slate-200">
      <div class="mx-auto max-w-4xl px-4">
        <div class="text-center mb-10">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            What Affects Junk Removal Cost?
          </h2>
          <p class="text-lg text-slate-600">
            Our transparent pricing considers these key factors
          </p>
        </div>

        <div class="space-y-4">
          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">Volume & Quantity</h3>
              <p class="text-slate-600">
                The amount of space your items take up in our truck-from a few
                boxes to a full load-directly impacts the final cost.
              </p>
            </div>
          </div>

          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">
                Weight & Type of Items
              </h3>
              <p class="text-slate-600">
                Heavy furniture and appliances require more labor than
                lightweight boxes. Hazardous materials have special disposal
                fees.
              </p>
            </div>
          </div>

          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">Access & Location</h3>
              <p class="text-slate-600">
                Basement items, walk-up apartments, or tight spaces require
                extra time and effort to safely remove your junk.
              </p>
            </div>
          </div>

          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">
                Demolition or Disassembly
              </h3>
              <p class="text-slate-600">
                If we need to tear down structures or take apart large items
                before removal, this adds time to the job.
              </p>
            </div>
          </div>

          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">Same-Day Service</h3>
              <p class="text-slate-600">
                Need it gone today? We offer same-day junk removal when our
                schedule allows.
              </p>
            </div>
          </div>
        </div>

        <div
          class="mt-10 p-6 bg-primary-main/10 border-l-4 border-primary-main rounded-r-lg"
        >
          <p class="text-lg text-slate-900">
            <strong>Ready for an accurate quote?</strong> Call
            <a
              :href="phoneNumberTel"
              class="font-bold text-primary-main hover:text-primary-dark underline"
              >(612) 424-0609</a
            >
            for a fast estimate. We provide upfront pricing before we start-no
            surprises.
          </p>
        </div>
      </div>
    </section>

    <!-- Why Choose Junk Guys MN -->
    <section class="bg-white py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Why Choose Junk Guys MN
          </h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Professional junk removal service you can trust
          </p>
        </div>
        <div class="grid md:grid-cols-4 gap-8">
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">🏠</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Locally Owned
            </h3>
            <p class="text-slate-600">
              Family-owned business serving the Twin Cities communities
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">⚡</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Same-Day Service
            </h3>
            <p class="text-slate-600">
              Quick response with same-day availability when you need it
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">♻️</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Eco-Friendly
            </h3>
            <p class="text-slate-600">
              We donate and recycle whenever possible to reduce landfill waste
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">💰</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Upfront Pricing
            </h3>
            <p class="text-slate-600">
              Transparent quotes with no hidden fees or surprises
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Service Areas -->
    <section class="bg-slate-50 py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">Service Areas</h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Providing junk removal services throughout Minneapolis, St. Paul,
            and surrounding Twin Cities communities
          </p>
        </div>
        <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
          <div class="bg-white p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/minneapolis/junk-removal"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Minneapolis</NuxtLink
            >
          </div>
          <div class="bg-white p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/saint-paul/junk-removal"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Saint Paul</NuxtLink
            >
          </div>
          <div class="bg-white p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/bloomington"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Bloomington</NuxtLink
            >
          </div>
          <div class="bg-white p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/eden-prairie"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Eden Prairie</NuxtLink
            >
          </div>
          <div class="bg-white p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/maple-grove"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Maple Grove</NuxtLink
            >
          </div>
          <div class="bg-white p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/brooklyn-park"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Brooklyn Park</NuxtLink
            >
          </div>
          <div class="bg-white p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/lakeville"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Lakeville</NuxtLink
            >
          </div>
          <div class="bg-white p-4 rounded-lg text-center">
            <NuxtLink
              to="/services"
              class="text-slate-700 hover:text-primary-main font-semibold"
              >+ More Areas</NuxtLink
            >
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="bg-white py-16">
      <div class="mx-auto max-w-4xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            FAQs About Junk Removal
          </h2>
          <p class="text-lg text-slate-600">
            Common questions about our junk removal service
          </p>
        </div>
        <div
          class="bg-white rounded-2xl shadow-sm border border-slate-200 divide-y divide-slate-200"
        >
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  What items can't you remove?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                We cannot remove hazardous materials like paint, chemicals,
                batteries, or medical waste. These require special handling and
                disposal methods. Contact your local waste management for
                guidance.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  Do you offer same-day service?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                Yes! We often have same-day availability in core Twin Cities
                areas. Call us early in the day to check availability and
                schedule your junk removal.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  How do you determine pricing?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                Pricing is based on the volume of space your items take up in
                our truck. We provide on-site pricing confirmation to ensure
                accuracy. Single items have flat rates.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  Do I need to be present during junk removal?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                While we prefer you're present to point out items and approve
                final pricing, it's not required. Many Minneapolis and St. Paul
                customers arrange for us to work while they're away. Just ensure
                items are accessible and payment is arranged in advance.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  How quickly can you come out?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                We often have same-day availability throughout the Twin Cities
                area. Call
                <a
                  :href="phoneNumberTel"
                  class="font-semibold text-primary-main hover:text-primary-dark underline"
                  >(612) 424-0609</a
                >
                early in the day to check our schedule. Next-day service is
                almost always available.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  What do you do with the junk you collect?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                We prioritize responsible disposal. Items in good condition are
                donated to local Twin Cities charities. Recyclable materials
                like metal, electronics, and cardboard are taken to appropriate
                facilities. Only unusable items go to landfills.
              </div>
            </div>
          </details>
        </div>
      </div>
    </section>

    <!-- Final CTA -->
    <section class="bg-primary-main text-white py-16">
      <div class="mx-auto max-w-7xl px-4 text-center">
        <h2 class="text-3xl lg:text-4xl font-bold mb-4">
          Ready to Reclaim Your Space?
        </h2>
        <p class="text-xl text-white mb-8 max-w-2xl mx-auto">
          Get your junk removal done today. Fast, reliable, and eco-friendly
          service across the Twin Cities.
        </p>
        <div class="flex flex-wrap justify-center gap-4">
          <a
            :href="phoneNumberTel"
            class="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-main font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
          >
            📞 Call {{ phoneNumberPretty }}
          </a>
          <NuxtLink
            to="/book"
            class="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-primary-main transition-all duration-200"
          >
            📅 Book Online
          </NuxtLink>
        </div>
      </div>
    </section>
  </div>
</template>
